import { pageContainerStyle } from '@commonStyle';
import { useResponsive } from '@hooks';
import { Platform, StyleSheet } from 'react-native'
import { couleursCET } from '../GestionCET/GestionCET.styles';

// Palette issue de la maquette (design system Ligne 360)
export const couleursValideur = {
    ...couleursCET,
    bleuClair: '#EEF3FB',
    bleuBord: '#D6E2F5',
    violetClair: '#EFEEFA',
    violetFonce: '#2F26A8',
    roseBg: '#FDECEC',
    roseFg: '#B42318',
    blanc: '#ffffff',
}

const ombre = (opacite: number, rayon: number, hauteur: number) => Platform.select({
    web: { boxShadow: `0 ${hauteur}px ${rayon}px rgba(15,23,42,${opacite})` },
    default: {
        shadowColor: '#0F172A',
        shadowOffset: { width: 0, height: hauteur },
        shadowOpacity: opacite,
        shadowRadius: rayon,
        elevation: 1,
    },
})

export default function useEspaceValideurStyle() {
    const isResponsive = useResponsive();
    const c = couleursValideur;
    return {
        isResponsive,
        styles: StyleSheet.create({
            container: {
                ...pageContainerStyle,
                backgroundColor: '#ffffff',
                paddingHorizontal: isResponsive ? 12 : 24,
                paddingTop: isResponsive ? 16 : 28,
                paddingBottom: 40,
                gap: 20,
            },
            sousTitrePage: {
                fontSize: 13,
                color: c.grey600,
            },
            // Planning du jour
            planning: {
                flexDirection: 'row',
                alignItems: 'center',
                flexWrap: isResponsive ? 'wrap' : 'nowrap',
                gap: 14,
                paddingVertical: 14,
                paddingHorizontal: 18,
                borderWidth: 1,
                borderColor: c.grey200,
                borderRadius: 12,
                backgroundColor: '#ffffff',
                ...ombre(0.06, 3, 1),
            },
            planningIcone: {
                width: 44,
                height: 44,
                borderRadius: 8,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: c.bleuClair,
            },
            planningTexte: {
                flex: 1,
                minWidth: 160,
            },
            planningTitre: {
                fontSize: 15,
                fontWeight: '600',
                color: c.grey800,
            },
            planningSousTitre: {
                marginTop: 3,
                fontSize: 12,
                color: c.grey600,
            },
            planningResume: {
                flexDirection: 'row',
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
                gap: 6,
            },
            pilule: {
                height: 24,
                paddingHorizontal: 10,
                borderRadius: 999,
                justifyContent: 'center',
            },
            piluleTexte: {
                fontSize: 12,
                fontWeight: '500',
            },
            // Barre d'outils
            barreOutils: {
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 12,
            },
            barreGauche: {
                flexDirection: 'row',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 12,
            },
            sectionTitre: {
                fontSize: 16,
                fontWeight: '600',
                color: c.grey800,
            },
            vues: {
                flexDirection: 'row',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 6,
            },
            vuesLabel: {
                fontSize: 12,
                color: c.grey600,
            },
            vue: {
                height: 32,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
                paddingHorizontal: 10,
                borderRadius: 4,
                borderWidth: 1.5,
            },
            vueTexte: {
                fontSize: 13,
                fontWeight: '500',
            },
            pastille: {
                width: 8,
                height: 8,
                borderRadius: 999,
            },
            filtres: {
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: 4,
                padding: 3,
                borderRadius: 6,
                backgroundColor: c.grey100,
            },
            filtre: {
                height: 32,
                paddingHorizontal: 12,
                borderRadius: 4,
                justifyContent: 'center',
            },
            filtreActif: {
                backgroundColor: '#ffffff',
                ...ombre(0.12, 2, 1),
            },
            filtreTexte: {
                fontSize: 13,
                fontWeight: '500',
                color: c.grey600,
            },
            filtreTexteActif: {
                color: c.bleu,
            },
            // Kanban
            kanban: {
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: 16,
            },
            colonne: {
                flex: isResponsive ? undefined : 1,
                width: isResponsive ? 290 : undefined,
                minWidth: 0,
                minHeight: 240,
                borderRadius: 12,
                borderWidth: 1,
                borderColor: c.grey200,
                backgroundColor: c.grey50,
                overflow: 'hidden',
            },
            colonneBarre: {
                height: 4,
            },
            colonneEnTete: {
                flexDirection: 'row',
                alignItems: 'center',
                gap: 8,
                paddingVertical: 12,
                paddingHorizontal: 14,
            },
            colonneTitre: {
                flex: 1,
                fontSize: 14,
                fontWeight: '600',
                color: c.grey800,
            },
            compteur: {
                minWidth: 24,
                height: 22,
                paddingHorizontal: 8,
                borderRadius: 999,
                alignItems: 'center',
                justifyContent: 'center',
            },
            compteurTexte: {
                fontSize: 12,
                fontWeight: '700',
            },
            colonneContenu: {
                flex: 1,
                gap: 8,
                paddingHorizontal: 10,
                paddingBottom: 12,
            },
            // Repousse les demandes à la chaîne de validation terminée en bas de la colonne
            termineesBloc: {
                marginTop: 'auto',
                gap: 8,
            },
            terminees: {
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
                marginTop: 4,
                paddingVertical: 6,
                paddingHorizontal: 4,
                borderTopWidth: 1,
                borderTopColor: c.grey300,
            },
            termineesTexte: {
                flex: 1,
                fontSize: 12,
                fontWeight: '600',
                color: c.grey600,
            },
            // Occupe la place libre de son conteneur pour y centrer le message
            colonneVide: {
                flex: 1,
                minHeight: 120,
                alignItems: 'center',
                justifyContent: 'center',
                padding: 20,
                borderWidth: 1.5,
                borderStyle: 'dashed',
                borderColor: c.grey300,
                borderRadius: 8,
            },
            // Section vide d'une colonne "Validées" ou "Refusées" (demandes modifiables ou chaîne terminée)
            sectionVide: {
                minHeight: 64,
                padding: 12,
            },
            colonneVideTexte: {
                fontSize: 13,
                color: c.grey600,
                textAlign: 'center',
            },
            // Sélection multiple
            caseACocher: {
                width: 18,
                height: 18,
                borderRadius: 4,
                borderWidth: 1.5,
                borderColor: c.grey400,
                backgroundColor: c.blanc,
                alignItems: 'center',
                justifyContent: 'center',
            },
            caseACocherActive: {
                borderColor: c.primaire,
                backgroundColor: c.primaire,
            },
            barreSelection: {
                flexDirection: 'row',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 10,
                paddingVertical: 10,
                paddingHorizontal: 14,
                borderWidth: 1,
                borderColor: c.primaire,
                borderRadius: 8,
                backgroundColor: c.violetClair,
            },
            barreSelectionTexte: {
                flex: 1,
                minWidth: 160,
                fontSize: 14,
                fontWeight: '600',
                color: c.violetFonce,
            },
            barreSelectionLien: {
                fontSize: 13,
                fontWeight: '500',
                color: c.violetFonce,
                textDecorationLine: 'underline',
            },
            barreSelectionBoutons: {
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: 8,
            },
            boutonGroupe: {
                height: 36,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
                paddingHorizontal: 12,
                borderWidth: 1,
                borderRadius: 4,
            },
            listeGroupee: {
                maxHeight: 260,
                borderWidth: 1,
                borderColor: c.grey200,
                borderRadius: 8,
            },
            ligneGroupee: {
                flexDirection: 'row',
                alignItems: 'center',
                gap: 10,
                paddingVertical: 10,
                paddingHorizontal: 12,
                borderTopWidth: 1,
                borderTopColor: c.grey200,
            },
            ligneGroupeePremiere: {
                borderTopWidth: 0,
            },
            ligneGroupeeTexte: {
                flex: 1,
                minWidth: 0,
            },
            // Carte de demande
            carte: {
                flexDirection: 'row',
                borderWidth: 1,
                borderColor: c.grey200,
                borderRadius: 8,
                backgroundColor: '#ffffff',
                overflow: 'hidden',
                ...ombre(0.04, 2, 1),
            },
            carteBarre: {
                width: 4,
            },
            carteContenu: {
                flex: 1,
                minWidth: 0,
                padding: 12,
                gap: 8,
            },
            carteLigne: {
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: 8,
            },
            carteIdentite: {
                flex: 1,
                minWidth: 0,
            },
            carteAgent: {
                fontSize: 14,
                fontWeight: '600',
                color: c.grey800,
            },
            carteNature: {
                marginTop: 3,
                fontSize: 12,
                fontWeight: '500',
            },
            carteType: {
                height: 20,
                paddingHorizontal: 8,
                borderRadius: 999,
                justifyContent: 'center',
            },
            carteTypeTexte: {
                fontSize: 11,
                fontWeight: '500',
            },
            carteDetail: {
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
            },
            cartePeriode: {
                flex: 1,
                minWidth: 0,
                fontSize: 13,
                color: c.grey600,
            },
            carteDuree: {
                fontSize: 13,
                fontWeight: '600',
                color: c.grey800,
                fontVariant: ['tabular-nums'],
            },
            carteBoutons: {
                flexDirection: 'row',
                gap: 6,
                paddingTop: 2,
            },
            carteBouton: {
                flex: 1,
                height: 32,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                borderWidth: 1,
                borderRadius: 4,
            },
            carteBoutonTexte: {
                fontSize: 13,
                fontWeight: '600',
            },
            carteCommentaire: {
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
            },
            carteCommentaireTexte: {
                flex: 1,
                minWidth: 0,
                fontSize: 12,
                color: c.grey600,
            },
            // Panneau de décision
            defilement: {
                flexGrow: 0,
                flexShrink: 1,
            },
            decision: {
                width: '100%',
                gap: 12,
            },
            decisionSousTitre: {
                fontSize: 12,
                color: c.grey600,
            },
            lienPlanning: {
                flexDirection: 'row',
                alignItems: 'center',
                gap: 10,
                paddingVertical: 10,
                paddingHorizontal: 12,
                borderWidth: 1,
                borderColor: c.grey200,
                borderRadius: 8,
                backgroundColor: c.grey50,
            },
            lienPlanningIcone: {
                width: 32,
                height: 32,
                borderRadius: 6,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: c.bleuClair,
            },
            lienPlanningTexte: {
                flex: 1,
                fontSize: 13,
                fontWeight: '600',
                color: c.grey800,
            },
            infos: {
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: 8,
            },
            info: {
                flexBasis: '47%',
                flexGrow: 1,
                minWidth: 0,
                paddingVertical: 12,
                paddingHorizontal: 14,
                borderWidth: 1,
                borderColor: c.grey200,
                borderRadius: 8,
            },
            infoLabel: {
                fontSize: 12,
                fontWeight: '600',
                color: c.grey600,
            },
            infoValeur: {
                marginTop: 5,
                fontSize: 14,
                fontWeight: '500',
                color: c.grey800,
            },
            bloc: {
                paddingVertical: 12,
                paddingHorizontal: 14,
                borderWidth: 1,
                borderColor: c.grey200,
                borderRadius: 8,
            },
            soldeEnTete: {
                flexDirection: 'row',
                alignItems: 'center',
                gap: 8,
            },
            soldeLabel: {
                flex: 1,
                minWidth: 0,
                fontSize: 12,
                fontWeight: '600',
                color: c.grey600,
            },
            boutonDetail: {
                width: 32,
                height: 32,
                borderRadius: 6,
                borderWidth: 1,
                alignItems: 'center',
                justifyContent: 'center',
            },
            soldeLigne: {
                flexDirection: 'row',
                alignItems: 'center',
                marginTop: 10,
                gap: 8,
            },
            soldeElement: {
                flex: 1,
                alignItems: 'center',
            },
            soldeElementLabel: {
                fontSize: 12,
                fontWeight: '500',
                color: c.grey600,
            },
            soldeValeur: {
                marginTop: 3,
                fontSize: 18,
                fontWeight: '700',
                color: c.grey800,
                fontVariant: ['tabular-nums'],
            },
            soldeFleche: {
                alignItems: 'center',
                gap: 4,
            },
            soldeFlecheIcone: {
                width: 32,
                height: 32,
                borderRadius: 999,
                alignItems: 'center',
                justifyContent: 'center',
            },
            soldeDelta: {
                fontSize: 12,
                fontWeight: '600',
                fontVariant: ['tabular-nums'],
            },
            soldeChargement: {
                marginTop: 10,
                fontSize: 13,
                color: c.grey600,
                textAlign: 'center',
            },
            // Détail du compteur
            detail: {
                marginTop: 12,
                paddingTop: 12,
                borderTopWidth: 1,
                borderTopColor: c.grey200,
            },
            detailTitre: {
                flexDirection: 'row',
                alignItems: 'center',
                gap: 8,
            },
            detailTitreTexte: {
                fontSize: 15,
                fontWeight: '700',
            },
            detailReste: {
                marginTop: 10,
                paddingVertical: 14,
                paddingHorizontal: 12,
                borderRadius: 8,
                backgroundColor: c.grey50,
                alignItems: 'center',
            },
            detailResteLabel: {
                fontSize: 13,
                color: c.grey600,
            },
            detailResteValeur: {
                marginTop: 4,
                fontSize: 26,
                fontWeight: '700',
                letterSpacing: -0.4,
                fontVariant: ['tabular-nums'],
            },
            detailColonnes: {
                flexDirection: 'row',
                flexWrap: 'wrap',
                rowGap: 10,
                marginTop: 12,
            },
            detailColonne: {
                flexGrow: 1,
                flexBasis: isResponsive ? '33%' : 0,
                alignItems: 'center',
                minWidth: 0,
            },
            detailColonneLabel: {
                fontSize: 12,
                color: c.grey600,
                textAlign: 'center',
            },
            detailColonneValeur: {
                marginTop: 4,
                fontSize: 15,
                fontWeight: '700',
                color: c.grey800,
                fontVariant: ['tabular-nums'],
            },
            detailPied: {
                marginTop: 10,
                fontSize: 12,
                fontStyle: 'italic',
                color: c.grey450,
                textAlign: 'center',
            },
            champ: {
                gap: 6,
            },
            champLabel: {
                fontSize: 12,
                fontWeight: '600',
                color: c.grey600,
            },
            obligatoire: {
                color: c.danger,
            },
            commentaire: {
                minHeight: 88,
                paddingVertical: 10,
                paddingHorizontal: 12,
                borderWidth: 1,
                borderColor: c.grey300,
                borderRadius: 4,
                fontSize: 14,
                lineHeight: 20,
                color: c.grey800,
                backgroundColor: '#ffffff',
                textAlignVertical: 'top',
            },
            commentaireErreur: {
                borderColor: c.danger,
            },
            commentaireLectureSeule: {
                backgroundColor: c.grey50,
            },
            messageErreur: {
                fontSize: 12,
                color: c.danger,
            },
            choix: {
                flexDirection: 'row',
                gap: 8,
            },
            boutonChoix: {
                flex: 1,
                height: 44,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                borderWidth: 1.5,
                borderColor: c.grey300,
                borderRadius: 4,
                backgroundColor: '#ffffff',
            },
            // Décision déjà prise par le valideur
            boutonChoixDesactive: {
                opacity: 0.5,
            },
            boutonChoixTexte: {
                fontSize: 14,
                fontWeight: '600',
                color: c.grey800,
            },
            boutonConfirmer: {
                height: 48,
                borderRadius: 4,
                alignItems: 'center',
                justifyContent: 'center',
            },
            boutonConfirmerTexte: {
                fontSize: 15,
                fontWeight: '600',
            },
        })
    }
}
